import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FormConfiguration } from "pax-ui/components";
import { Emoji } from "projects/pax-ui/components/emoji/emoji-alt-mapping";
import { Observable } from "rxjs";
import { ProductSpecificCustomRouteData } from "src/app/shared/routes/shared/shared-route.constant";
import { DataAnalyticsEventPublisherService } from "src/app/shared/services/data-analytics/data-analytics-event-publisher.service";
import {
  FormComponentService,
  TermsAcceptedFormField,
} from "src/app/shared/services/form/form-component.service";
import { LanguageService } from "src/app/shared/services/language/language.service";
import { RouteDataService } from "src/app/shared/services/route-data.service";
import { RouterService } from "src/app/shared/services/router.service";
import { ProductConfigurationFacade } from "src/app/shared/store/quote/product-configuration.facade";
import { ProductConstraintsFacade } from "src/app/shared/store/quote/product-constraints.facade";
import {
  QuoteStoreFacade,
  SubmitParameters,
} from "src/app/shared/store/quote/quote-store.facade";
import { QuoteFacade } from "src/app/shared/store/quote/quote.facade";
import { Gender } from "src/app/shared/store/quote/shared/shared.state";
import {
  CheckoutPage,
  InfoDialogWithLegalDisclaimer,
} from "../checkout-page/checkout-page";

@Component({
  selector: "app-base",
  imports: [CheckoutPage, CommonModule],
  templateUrl: "./base.page.html",
  styleUrls: ["./base.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class BasePage {
  public readonly productSpecificCustomRouteData: ProductSpecificCustomRouteData;

  public hasBeenSubmitted = false;
  public formConfiguration!: FormConfiguration;
  public emojis: Emoji[] | undefined;
  public infoDialog?: InfoDialogWithLegalDisclaimer;
  public data$!: Observable<PremiumFormModel>;
  public enableBackButton = true;
  public enableSkipButton = false;
  public consultationAppointmentUrl: URL;

  constructor(
    readonly _activatedRoute: ActivatedRoute,
    private readonly _routeDataService: RouteDataService,
    private readonly _routerService: RouterService,
    protected quoteStoreFacade: QuoteStoreFacade,
    protected productConfigurationFacade: ProductConfigurationFacade,
    protected productConstraintsFacade: ProductConstraintsFacade,
    protected readonly formComponentService: FormComponentService,
    protected readonly quoteFacade: QuoteFacade,
    protected readonly dataAnalyticsEventPublisherService: DataAnalyticsEventPublisherService,
    protected readonly languageService: LanguageService,
  ) {
    this.productSpecificCustomRouteData =
      this._routeDataService.getProductSpecificCustomRouteData(
        this._activatedRoute,
      );

    const language = this.languageService.getLanguage();

    this.consultationAppointmentUrl =
      this.quoteFacade.getExternalConsultationAppointmentUrl(
        this.productSpecificCustomRouteData.product,
        language,
      );

    this.pushFunnelViewEvent();
  }

  protected initialize(formConfiguration: FormConfiguration) {
    this.formConfiguration = formConfiguration;
  }

  protected async onNavigateBack() {
    await this._routerService.tryNavigateToPage(
      this.productSpecificCustomRouteData.navigation.previousNavigationPage,
    );
  }

  protected async onNavigateNext() {
    await this._routerService.tryNavigateToPage(
      this.productSpecificCustomRouteData.navigation.nextNavigationPage,
    );
  }

  protected onFormSubmit(premiumForm: PremiumFormModel | null) {
    if (!premiumForm) {
      return;
    }

    this.hasBeenSubmitted = true;
    const submitParameters: SubmitParameters = {
      formModelContainer: {
        premiumForm: premiumForm,
      },
      originPage: this.productSpecificCustomRouteData.page,
      nextNavigationPage:
        this.productSpecificCustomRouteData.navigation.nextNavigationPage,
    };

    this.quoteStoreFacade.dispatchSubmitQuote(
      this.productSpecificCustomRouteData.product,
      submitParameters,
    );
  }

  protected onInternalFormSubmit(premiumFormModel: PremiumFormModel | null) {
    this.onFormSubmit(premiumFormModel);
  }

  protected onFormChange(_premiumForm: PremiumFormModel | null) {}

  private pushFunnelViewEvent() {
    const page = this.productSpecificCustomRouteData.page;
    const funnelStepNumber =
      this.productSpecificCustomRouteData.dataAnalytics.funnelStepNumber;
    this.dataAnalyticsEventPublisherService.pushFunnelViewEvent(
      this.productSpecificCustomRouteData.product,
      page,
      funnelStepNumber,
    );
  }
}

export interface PremiumFormModel {
  // Email Page
  email?: string | undefined;
  mobilePhoneNumber?: string | undefined;
  acceptedTerms?: TermsAcceptedFormField;

  // Gender Page
  gender?: Gender | undefined;

  // DateOfBirth Page
  dateOfBirth?: Date | undefined;

  // Smoker Page
  isSmoker?: boolean | undefined;

  // Insured Sum Page
  insuredSum?: number | undefined;

  // Insurance Term Page
  insuranceTermInYears?: InsuranceTermInYearsFormFieldOptions;
  customInsuranceTermInYears?: CustomInsuranceTermInYearsFormFieldOptions;

  // Backend Properties
  netPremium?: number | undefined;
}

export type InsuranceTermInYearsFormFieldOptions = number | "other" | undefined;
export type CustomInsuranceTermInYearsFormFieldOptions = number | undefined;
